import { ApplicationFormType, ApplicationStep } from '@brighte/validation';
import { User } from '../provider';
import {
  Applicant,
  Application,
  Category,
  FinancialProduct,
  Quote,
  QuoteStatus,
  RepaymentTermInput,
  Vendor,
} from './generated/graphql';

type ApplicationQuote = {
  repaymentOptions?: RepaymentTermInput[] | null;
  vendor: Pick<Vendor, 'tradingName' | 'publicId'>;
  category: Pick<Category, 'name' | 'group'>;
  financialProduct: Omit<FinancialProduct, 'categories'>;
  application?:
    | (Omit<Application, 'primaryApplicant' | 'documents' | 'status'> & {
        primaryApplicant: Pick<Applicant, 'firstName' | 'lastName' | 'address'>;
      })
    | null;
} & Omit<Quote, 'repaymentOptions' | 'vendor' | 'category' | 'application'>;

export type FormValues = ApplicationFormType & {
  applicationId: string;
  quote: ApplicationQuote;
  application: Partial<Application>;
};

export type CreateApplicationProps = {
  formValues: Partial<FormValues>;
  user?: Partial<User | null>;
  quote?: Partial<ApplicationQuote>;
};

export type UpdateApplicationProps = CreateApplicationProps & {
  currentPage: ApplicationStep;
};

export type FileWithPreview = {
  isUploaded?: boolean;
  preview: string;
  path: string;
  id: string;
};

export type PrePopValues = {
  email?: string;
  phone?: string;
  verificationType: string;
  financialProduct: Partial<FinancialProduct>;
  maskedEmail?: string;
  maskedPhone?: string;
  status: QuoteStatus;
};

export type ErrorResponse<TData = any> = {
  // graphql-request throws this shape for a GraphQL-layer error on an HTTP 200 (`errors` present,
  // `status` typically 200) as well as for a raw HTTP failure with no GraphQL body at all (e.g. a
  // 502 from the gateway/LB) — `errors` is absent then and `status` carries the real HTTP status.
  response: {
    status?: number;
    errors?: {
      message: string;
      path: string[];
      extensions?: {
        code?: string;
        message?: string;
        statusCode?: number;
        response?: {
          status?: number;
          statusText?: string;
          body: {
            code?: string;
            message?: string;
          };
        };
      };
    }[];
    data?: TData;
  };
  request: object;
};

type ErrorWithMessage = {
  message: string;
};

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as Record<string, unknown>).message === 'string';

const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
};

export const getErrorMessage = (error: unknown) => toErrorWithMessage(error).message;
